import { graphql } from '@apollo/client/react/hoc';
import Typography from '@material-ui/core/Typography';
import React, { useState, useEffect } from 'react';
import { compose, mapProps, withProps } from 'recompose';
import { Link, withRouter } from 'react-router-dom';
import { distanceInWords } from 'date-fns';
import { getNestedValue } from '@/se/utilities/data/object';
import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import Modal from '@/se/components/Modal';
import RouterLink from '@/se/components/RouterLink';
import { TruncatedText } from '@/se/components/typography';
import { isArray, isObject } from '@/se/utilities/check';
import AutoSubmittingForm from '@/se/components/AutoSubmittingForm';
import { sortBy, sortUndefinedValuesFirst } from '@/se/utilities/data/array';

import scheme, { approveScreen, removeScreen } from '../../../graph/screens';
import { list as roomList } from '../../../graph/rooms';
import { OR, ROOM_TYPE_LABELS, ROOM_TYPES } from '../room/enums';
import {
  ANALYTICS,
  CHARTING_TABLET,
  EXIT_DISPLAY,
  INTERNAL_MONITOR,
  INTERNAL_MONITOR_LABELS,
  OPERATION_ROOM_MONITOR,
  PACU_TABLET,
  POST_OP_TABLET,
  PREP_TABLET,
  REVIEW_DISPLAY,
  SCHEDULE_MONITOR,
  WAITING_ROOM_MONITOR,
  WAITING_ROOM_TABLET,
  HOSPITAL_OVERVIEW,
  STAFF_MONITOR,
  PREOP_BOARD,
} from './enums';
import AssignMonitoringScreenInput from '../../inputs/screen/AssignMonitoringScreenInput';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import ScreenConfig from './ScreenConfig';
import Tooltip from '../../Tooltip';
import { withSession } from '@/state/Session';
import ScreenMobileListItem from './ScreenMobileListItem';
import { DefaultTitle } from '@/se/components/entity/EntityRouter';
import pluralize from 'pluralize';
import { titlecase } from 'stringcase';
import { alpha, withStyles } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { Alert } from '@material-ui/lab';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import config from '../../../config';
import { withScope } from '@/contexts/ScopeContext';

const Status = withStyles(theme => ({
  root: {
    width: '.875rem',
    height: '.875rem',
    borderRadius: '50%',
    marginRight: theme.spacing(1),
    backgroundColor: props => (props.active ? theme.palette.success.main : alpha(theme.palette.text.disabled, 0.25)),
  },
}))(Box);

export const Address = withStyles(theme => ({
  root: {
    display: 'inline-block',
    backgroundColor: theme.palette.grey.A100,
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontWeight: 'bold',
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(-0.5, 0.5),
    userSelect: 'all',
  },
}))(Box);

const mapOperationRooms = props => ({
  ...props,
  operationRooms: isArray(getNestedValue('data.rooms', props)) ? props.data.rooms.filter(room => room.type === OR) : [],
  pacuRooms: isArray(getNestedValue('data.rooms', props))
    ? props.data.rooms.filter(room => room.type === ROOM_TYPES.PACU)
    : [],
  prepRooms: isArray(getNestedValue('data.rooms', props))
    ? props.data.rooms.filter(room => room.type === ROOM_TYPES.PRE_OP)
    : [],
  postOpRooms: isArray(getNestedValue('data.rooms', props))
    ? props.data.rooms.filter(room => room.type === ROOM_TYPES.POST_OP)
    : [],
});

const mapAssignOptions = props => ({
  ...props,
  options: [
    {
      value: { type: INTERNAL_MONITOR },
      label: INTERNAL_MONITOR_LABELS[INTERNAL_MONITOR],
    },
    {
      value: { type: WAITING_ROOM_MONITOR },
      label: INTERNAL_MONITOR_LABELS[WAITING_ROOM_MONITOR],
    },
    {
      value: { type: SCHEDULE_MONITOR },
      label: INTERNAL_MONITOR_LABELS[SCHEDULE_MONITOR],
    },
    ...(getNestedValue('operationRooms', props) || []).reduce(
      (acc, or) => [
        ...acc,
        {
          value: { type: OPERATION_ROOM_MONITOR, room: or.id },
          label: `${INTERNAL_MONITOR_LABELS[OPERATION_ROOM_MONITOR]} - ${or.name}`,
        },
      ],
      []
    ),
    ...(getNestedValue('prepRooms', props) || []).reduce(
      (acc, prepRooms) => [
        ...acc,
        {
          value: { type: PREP_TABLET, room: prepRooms.id },
          label: `${INTERNAL_MONITOR_LABELS[PREP_TABLET]} - ${prepRooms.name}`,
        },
      ],
      []
    ),
    ...(getNestedValue('pacuRooms', props) || []).reduce(
      (acc, pacuRooms) => [
        ...acc,
        {
          value: { type: PACU_TABLET, room: pacuRooms.id },
          label: `${INTERNAL_MONITOR_LABELS[PACU_TABLET]} - ${pacuRooms.name}`,
        },
      ],
      []
    ),
    ...(props?.scope?.hospital?.modules?.hasPostop
      ? (getNestedValue('postOpRooms', props) || []).reduce(
          (acc, postOpRooms) => [
            ...acc,
            {
              value: { type: POST_OP_TABLET, room: postOpRooms.id },
              label: `${INTERNAL_MONITOR_LABELS[POST_OP_TABLET]} - ${postOpRooms.name}`,
            },
          ],
          []
        )
      : []),
    {
      value: { type: CHARTING_TABLET },
      label: INTERNAL_MONITOR_LABELS[CHARTING_TABLET],
    },
    {
      value: { type: REVIEW_DISPLAY },
      label: INTERNAL_MONITOR_LABELS[REVIEW_DISPLAY],
    },
    {
      value: { type: EXIT_DISPLAY },
      label: INTERNAL_MONITOR_LABELS[EXIT_DISPLAY],
    },
    {
      value: { type: WAITING_ROOM_TABLET },
      label: INTERNAL_MONITOR_LABELS[WAITING_ROOM_TABLET],
    },
    {
      value: { type: ANALYTICS },
      label: INTERNAL_MONITOR_LABELS[ANALYTICS],
    },
    {
      value: { type: HOSPITAL_OVERVIEW },
      label: INTERNAL_MONITOR_LABELS[HOSPITAL_OVERVIEW],
    },
    {
      value: { type: STAFF_MONITOR },
      label: INTERNAL_MONITOR_LABELS[STAFF_MONITOR],
    },
    {
      value: { type: PREOP_BOARD },
      label: INTERNAL_MONITOR_LABELS[PREOP_BOARD],
    },
  ],
});

const ScreenAddress = ({ organizationId }) => <Address>{`${config.publicURL}/kiosk/${organizationId}`}</Address>;

const withOrganizationId = Component =>
  compose(
    withSession(unpackSessionObject),
    withRouter,
    withProps(props => ({
      organizationId:
        props.isSuperAdmin || props.isGroupAdmin ? props.match.params['organizationId'] : props.organization,
    }))
  )(Component);

const ScreenAddressHint = withOrganizationId(({ organizationId }) => (
  <Alert severity="info">
    {'Configure one of the displays to open '}
    <ScreenAddress organizationId={organizationId} />
    {' to set up a screen.'}
  </Alert>
));

const Empty = {
  illustration: theme => theme.illustrations.waiting,
  hint: <ScreenAddressHint />,
};

/** Calculate uptime from lastConnectedAt timestamp (for active connections) */
const calculateUptime = (lastConnectedAt, isActive) => {
  if (!lastConnectedAt || !isActive) return 'N/A';

  try {
    const connectedTime = new Date(lastConnectedAt);
    const now = new Date();
    const uptimeSeconds = Math.floor((now - connectedTime) / 1000);

    if (uptimeSeconds < 0) return 'N/A';

    const days = Math.floor(uptimeSeconds / 86400);
    const hours = Math.floor((uptimeSeconds % 86400) / 3600);
    const minutes = Math.floor((uptimeSeconds % 3600) / 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.length > 0 ? parts.join(' ') : '< 1m';
  } catch (_e) {
    return 'N/A';
  }
};

/** Format relative time with backward compatibility */
const formatRelativeTime = timestamp => {
  if (!timestamp) return 'Never';
  try {
    return distanceInWords(new Date(), timestamp, { addSuffix: true });
  } catch (_e) {
    return 'Invalid date';
  }
};

const screenStatusContent = (
  active,
  clientVersion,
  lastDisconnectedAt,
  lastConnectedAt,
  connectionCount,
  ipAddress
) => {
  const parts = [];
  if (active) {
    parts.push(`Client version: ${(clientVersion || 'Unknown').substring(0, 8)}`);
    if (lastConnectedAt) {
      parts.push(`Connected: ${formatRelativeTime(lastConnectedAt)}`);
    }
    if (connectionCount) {
      parts.push(`Connections: ${connectionCount}`);
    }
    // Show IP address only in debug mode
    if (globalDebugMode && ipAddress) {
      parts.push(`IP: ${ipAddress}`);
    }
  } else {
    parts.push(`Last Reported: ${formatRelativeTime(lastDisconnectedAt)}`);
    if (lastConnectedAt) {
      parts.push(`Last Connected: ${formatRelativeTime(lastConnectedAt)}`);
    }
    // Show IP address only in debug mode
    if (globalDebugMode && ipAddress) {
      parts.push(`IP: ${ipAddress}`);
    }
  }
  return parts.join(' • ');
};

const ScreenStatus = ({
  type,
  active,
  clientVersion,
  lastDisconnectedAt,
  lastConnectedAt,
  connectionCount,
  ipAddress,
}) => {
  const [debugMode, setDebugMode] = useState(globalDebugMode);

  // Subscribe to debug mode changes to update tooltip
  useEffect(() => {
    debugModeListeners.push(setDebugMode);
    return () => {
      const index = debugModeListeners.indexOf(setDebugMode);
      if (index > -1) debugModeListeners.splice(index, 1);
    };
  }, []);

  return (
    <Box display="flex" alignItems="center">
      <Tooltip
        content={screenStatusContent(
          active,
          clientVersion,
          lastDisconnectedAt,
          lastConnectedAt,
          connectionCount,
          debugMode ? ipAddress : null
        )}
      >
        <Status active={active ? 1 : 0} />
      </Tooltip>

      <TruncatedText>
        {[
          INTERNAL_MONITOR,
          WAITING_ROOM_MONITOR,
          SCHEDULE_MONITOR,
          PACU_TABLET,
          PREP_TABLET,
          POST_OP_TABLET,
          CHARTING_TABLET,
          REVIEW_DISPLAY,
          EXIT_DISPLAY,
          WAITING_ROOM_TABLET,
          ANALYTICS,
          HOSPITAL_OVERVIEW,
          STAFF_MONITOR,
          PREOP_BOARD,
        ].includes(type.type)
          ? INTERNAL_MONITOR_LABELS[type.type]
          : type.type === OPERATION_ROOM_MONITOR
            ? getNestedValue('room.name', type) || ROOM_TYPE_LABELS.OR
            : 'Unknown monitoring screen'}
      </TruncatedText>
    </Box>
  );
};
const AssignAction = withRouter(({ match, data }) => (
  <RouterLink to={`${match.url}/assign/${data.code}`}>Assign</RouterLink>
));

const ConfigAction = withRouter(({ match, data }) => (
  <Box my={-1}>
    <Button color="primary" component={Link} to={`${match.url}/configure/${data.code}`}>
      Configure
    </Button>
  </Box>
));

const AssignScreen = ({ match, history, baseUrl, options, approveScreen }) => {
  const screenId = getNestedValue('params.id', match);
  const handleSubmit = value => {
    approveScreen({ variables: { screenCode: screenId, screenType: value } });
    history.push(baseUrl);
  };

  const Input = ({ onChange, value }) => (
    <AssignMonitoringScreenInput
      value={value}
      name={'assign-monitoring-screen'}
      getOptionValue={value => (isObject(value) ? `${value.type}${value.room ? value.room : ''}` : undefined)}
      options={options}
      onChange={onChange}
    />
  );

  return (
    <Modal title="Assign Screen" closeTo={baseUrl}>
      <AutoSubmittingForm input={Input} onSubmit={handleSubmit} />
    </Modal>
  );
};

const RemoveScreen = ({ data, removeScreen }) => (
  <Box my={-1}>
    <Button
      color="primary"
      onClick={() =>
        window.confirm('Deleting a screen is permanent. Proceed?') && removeScreen({ variables: { screenCode: data } })
      }
    >
      Remove
    </Button>
  </Box>
);

const Title = ({ entityName, isEmpty }) => (
  <Box display="flex" alignItems="center">
    <Box mr={2}>
      <DefaultTitle>{titlecase(pluralize(entityName))}</DefaultTitle>
    </Box>
    {!isEmpty && <ScreenAddressHint />}
  </Box>
);

// Global debug mode state (simple approach for this feature)
let globalDebugMode = false;
const debugModeListeners = [];

const toggleDebugMode = () => {
  globalDebugMode = !globalDebugMode;
  debugModeListeners.forEach(listener => listener(globalDebugMode));
};

/** Filter component for screens with MUI components */
const ScreenFilter = ({ value = {}, onChange }) => {
  const [debugMode, setDebugMode] = useState(globalDebugMode);

  // Use 'value' prop (standard for FilterComponent) or fallback to empty object
  const filter = value || {};

  // Listen for Shift+D to toggle debug mode
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.shiftKey && event.key === 'D') {
        event.preventDefault();
        toggleDebugMode();
      }
    };

    // Subscribe to debug mode changes
    debugModeListeners.push(setDebugMode);

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      const index = debugModeListeners.indexOf(setDebugMode);
      if (index > -1) debugModeListeners.splice(index, 1);
    };
  }, []);

  const handleStatusChange = event => {
    const selectedValue = event.target.value;
    const newFilter = { ...filter };
    if (selectedValue === 'all') {
      delete newFilter.status;
    } else {
      newFilter.status = selectedValue;
    }
    onChange(newFilter);
  };

  const handleSearchChange = event => {
    const searchValue = event.target.value;
    const newFilter = { ...filter };
    if (searchValue === '') {
      delete newFilter.search;
    } else {
      newFilter.search = searchValue;
    }
    onChange(newFilter);
  };

  const handleTypeChange = event => {
    const selectedValue = event.target.value;
    const newFilter = { ...filter };
    if (selectedValue === 'all') {
      delete newFilter.type;
    } else {
      newFilter.type = selectedValue;
    }
    onChange(newFilter);
  };

  const handleClearFilters = () => {
    onChange({});
  };

  const hasActiveFilters = filter.status || filter.search || filter.type;

  return (
    <Box mb={3}>
      {debugMode && (
        <Box mb={1}>
          <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic' }}>
            🔧 Debug Mode Active (Press Shift+D to toggle)
          </Typography>
        </Box>
      )}
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="status-filter-label">Filter by Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={filter?.status || 'all'}
              onChange={handleStatusChange}
              label="Filter by Status"
            >
              <MenuItem value="all">All Screens</MenuItem>
              <MenuItem value="assigned">Assigned Only</MenuItem>
              <MenuItem value="unassigned">Unassigned Only</MenuItem>
              <MenuItem value="online">Online Only</MenuItem>
              <MenuItem value="offline">Offline Only</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="type-filter-label">Filter by Type</InputLabel>
            <Select
              labelId="type-filter-label"
              id="type-filter"
              value={filter?.type || 'all'}
              onChange={handleTypeChange}
              label="Filter by Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value={SCHEDULE_MONITOR}>Schedule</MenuItem>
              <MenuItem value={INTERNAL_MONITOR}>Internal Monitor</MenuItem>
              <MenuItem value={WAITING_ROOM_MONITOR}>Waiting Room</MenuItem>
              <MenuItem value={OPERATION_ROOM_MONITOR}>OR Monitor</MenuItem>
              <MenuItem value={PACU_TABLET}>PACU Tablet</MenuItem>
              <MenuItem value={PREP_TABLET}>Prep Tablet</MenuItem>
              <MenuItem value={POST_OP_TABLET}>Post-Op Tablet</MenuItem>
              <MenuItem value={CHARTING_TABLET}>Charting Tablet</MenuItem>
              <MenuItem value={REVIEW_DISPLAY}>Review Display</MenuItem>
              <MenuItem value={EXIT_DISPLAY}>Exit Display</MenuItem>
              <MenuItem value={WAITING_ROOM_TABLET}>Waiting Room Tablet</MenuItem>
              <MenuItem value={ANALYTICS}>Analytics</MenuItem>
              <MenuItem value={HOSPITAL_OVERVIEW}>Hospital Overview</MenuItem>
              <MenuItem value={STAFF_MONITOR}>Staff Monitor</MenuItem>
              <MenuItem value={PREOP_BOARD}>PreOP Board</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Search by Description or Code"
            placeholder="e.g., OR1, hallway, room 3..."
            value={filter?.search || ''}
            onChange={handleSearchChange}
          />
        </Grid>
        {hasActiveFilters && (
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="outlined" size="medium" onClick={handleClearFilters} style={{ height: '40px' }}>
              Clear Filters
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Get columns based on debug mode
const getColumns = debugMode => {
  const baseColumns = [
    {
      key: 'code',
      title: 'Code',
      lens: item => item.code,
      Component: props => <Typography>{props.data}</Typography>,
    },
    {
      key: 'connection',
      title: 'Status & Type',
      lens: item => item,
      Component: props => {
        const connection = getNestedValue('data.connection', props);
        const clientVersion = getNestedValue('data.connection.clientVersion', props);

        return connection ? (
          <ScreenStatus {...connection} clientVersion={clientVersion} />
        ) : (
          <AssignAction {...props} />
        );
      },
    },
    {
      key: 'configuration.description',
      title: 'Description',
      lens: item => getNestedValue('configuration.description', item),
      Component: ({ data }) => <TruncatedText>{data}</TruncatedText>,
    },
    {
      key: 'connection.lastConnectedAt',
      title: 'Last Connected',
      lens: item => getNestedValue('connection.lastConnectedAt', item),
      Component: ({ data }) => (
        <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
          {data ? formatRelativeTime(data) : 'Never'}
        </Typography>
      ),
    },
  ];

  // Debug-only columns (IP Address, Connections, Uptime)
  const debugColumns = [
    {
      key: 'connection.ipAddress',
      title: 'IP Address 🔧',
      lens: item => getNestedValue('connection.ipAddress', item),
      Component: ({ data }) => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {data || 'N/A'}
        </Typography>
      ),
    },
    {
      key: 'connection.connectionCount',
      title: 'Connections 🔧',
      lens: item => getNestedValue('connection.connectionCount', item),
      Component: ({ data }) => (
        <Typography variant="body2" align="right">
          {data || 0}
        </Typography>
      ),
    },
    {
      key: 'connection.uptime',
      title: 'Uptime 🔧',
      lens: item => item,
      Component: ({ data }) => {
        const connection = getNestedValue('connection', data);
        const [uptime, setUptime] = useState(calculateUptime(connection?.lastConnectedAt, connection?.active));

        // Update uptime every minute for active connections
        useEffect(() => {
          if (connection?.active && connection?.lastConnectedAt) {
            const interval = setInterval(() => {
              setUptime(calculateUptime(connection.lastConnectedAt, connection.active));
            }, 60000);

            return () => clearInterval(interval);
          }
        }, [connection?.active, connection?.lastConnectedAt]);

        return (
          <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
            {uptime}
          </Typography>
        );
      },
    },
  ];

  const endColumns = [
    {
      key: 'data.connection',
      title: '',
      lens: item => item,
      Component: props => {
        const connection = getNestedValue('data.connection', props);
        return connection && <ConfigAction {...props} />;
      },
    },
    {
      key: 'removeScreen',
      title: '',
      lens: item => item.code,
      Component: compose(graphql(removeScreen, { name: 'removeScreen' }))(RemoveScreen),
    },
  ];

  // Include debug columns only in debug mode
  return debugMode ? [...baseColumns, ...debugColumns, ...endColumns] : [...baseColumns, ...endColumns];
};

export default GraphQLCRUD({
  entityName: 'Screen',
  scheme,
  List: {
    Title,
    tableKey: 'Screens',
    MobileItemComponent: ScreenMobileListItem,
    FilterComponent: ScreenFilter,
    columns: getColumns(globalDebugMode), // Use dynamic columns based on debug mode
    Empty,
    additionalRoutes: [
      {
        key: 'assign-screen',
        path: '/assign/:id',
        Component: compose(
          graphql(roomList),
          withScope,
          mapProps(mapOperationRooms),
          mapProps(mapAssignOptions),
          graphql(approveScreen, { name: 'approveScreen' })
        )(AssignScreen),
      },
      {
        key: 'config-screen',
        path: '/configure/:id',
        Component: ScreenConfig,
      },
    ],
    hideColumns: false,
    mapListProps: ({ data, getList, entityNamePlural, filter = {}, ...rest }) => {
      let screens = getList(data) || [];

      // Apply filtering based on status
      if (filter.status && filter.status !== 'all') {
        screens = screens.filter(screen => {
          const hasConnection = !!screen.connection;
          const isOnline = hasConnection && screen.connection.active;

          switch (filter.status) {
            case 'assigned':
              return hasConnection;
            case 'unassigned':
              return !hasConnection;
            case 'online':
              return isOnline;
            case 'offline':
              return hasConnection && !isOnline;
            default:
              return true;
          }
        });
      }

      // Apply type filter
      if (filter.type) {
        screens = screens.filter(screen => screen.connection?.type?.type === filter.type);
      }

      // Apply search filter (description, code, or screen type)
      if (filter.search) {
        const searchLower = filter.search.toLowerCase();
        screens = screens.filter(screen => {
          // Search in code
          if (screen.code && screen.code.toLowerCase().includes(searchLower)) {
            return true;
          }
          // Search in description
          if (
            screen.configuration?.description &&
            screen.configuration.description.toLowerCase().includes(searchLower)
          ) {
            return true;
          }
          // Search in screen type
          if (screen.connection?.type?.type) {
            const typeLabel = INTERNAL_MONITOR_LABELS[screen.connection.type.type] || screen.connection.type.type;
            if (typeLabel.toLowerCase().includes(searchLower)) {
              return true;
            }
          }
          // Search in room name
          if (
            screen.connection?.type?.room?.name &&
            screen.connection.type.room.name.toLowerCase().includes(searchLower)
          ) {
            return true;
          }
          return false;
        });
      }

      // Sort: unassigned first, then by connection status
      screens = screens.sort(sortBy('connection')(sortUndefinedValuesFirst));

      return {
        ...rest,
        data: {
          ...data,
          [entityNamePlural]: screens,
        },
        columns: getColumns(globalDebugMode), // Inject dynamic columns
      };
    },
  },
});
